import { get, getBlob, http, post, put } from '@utilities';
import { type AxiosResponse } from 'axios';
import type { CallHistoryResponse, InitiateCallResponse } from './exotelTypes';

export const getLeads = async (data: Record<string, any>): Promise<AxiosResponse> => await post('lead/fetch/all', data);
export const createLead = async (data: Record<string, any>): Promise<AxiosResponse> => await post('lead', data);
export const getLeadDetails = async (leadID: any): Promise<AxiosResponse> => await get(`lead/${leadID}`);
export const getLeadProjects = async (leadID: any): Promise<AxiosResponse> => await post(`project/lead/${leadID}`);
export const updateLeadDetails = async (data: any, leadID): Promise<AxiosResponse> => await put(`lead/${leadID}`, data);
export const autocompleteLead = async (prefix: any): Promise<AxiosResponse> =>
	await get(`lead/autocomplete?prefix=${prefix}`);
export const addLeadQuotation = async (data: Record<string, any>): Promise<AxiosResponse> => await post('lead/quotation', data);
export const deleteQuotationProductItem = async (itemId): Promise<AxiosResponse> =>
	await put(`quotation/product/${itemId}/delete`);
export const deleteQuotationVersion = async (itemId): Promise<AxiosResponse> =>
	await put(`quotation/version/${itemId}/delete`);
export const fetchLeadQuotation = async (data: Record<string, any>): Promise<AxiosResponse> => await post('quotation/all', data);
export const fetchQuotationToolList = async (quotationID: any): Promise<AxiosResponse> =>
	await get(`quotation/${quotationID}`);

export type ParametricQuotationState = {
	builder?: string;
	productName?: string;
	legacyFreeform?: import('@components/FreeformBuilderDialog/types').FreeformCalculateResponse;
	quotationId: string;
	productId: string;
	builderRevision: number;
	variantType?: string;
	variantId?: string;
	variantName?: string;
	variantVersion?: number;
	parametricDefinition?: Record<string, unknown>;
	parametricFormula?: Record<string, unknown>;
	parametricFormulaHash?: string;
	parametricDefaultMaterials?: Record<string, unknown>;
	materialSelections?: Record<string, unknown>;
	builderInputs?: Record<string, unknown>;
	instanceDocument?: Record<string, unknown>;
	freeformCommands?: Array<Record<string, unknown>>;
	cutlist?: Array<Record<string, unknown>>;
	pricing?: Record<string, number>;
	measurement?: Record<string, number | null>;
	price?: number;
	quantity?: number;
};

export type ParametricQuotationStateRequest = {
	measurement?: { width: number; height: number; depth: number };
	legacyFreeformRevision?: number;
	baseRevision?: number;
	instanceDocument?: Record<string, unknown>;
	materialSelections?: Record<string, unknown>;
	builderInputs?: Record<string, unknown>;
	freeformCommands?: Array<Record<string, unknown>>;
	cutlist?: Array<Record<string, unknown>>;
	pricingRequest?: Record<string, unknown>;
};

export const getParametricQuotationState = async (
	quotationId: string | number,
	productId: string
): Promise<AxiosResponse<ParametricQuotationState>> =>
	await get(`quotation/${quotationId}/products/${encodeURIComponent(productId)}/parametric-state`);

export const previewParametricQuotationPrice = async (
	quotationId: string | number,
	productId: string,
	data: ParametricQuotationStateRequest
): Promise<AxiosResponse<ParametricQuotationState>> =>
	await post(`quotation/${quotationId}/products/${encodeURIComponent(productId)}/parametric-price-preview`, data);

export const applyParametricQuotationState = async (
	quotationId: string | number,
	productId: string,
	data: ParametricQuotationStateRequest
): Promise<AxiosResponse<ParametricQuotationState>> =>
	await put(`quotation/${quotationId}/products/${encodeURIComponent(productId)}/parametric-state`, data);

export const sendUploadQuotation = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await post('quotation/send_email', data);
export const updateLeadQuotation = async (data): Promise<AxiosResponse> => {
	try {
		const res = await http.post(`quotation/update`, data);
		return res;
	} catch (e: any) {
		const status = e?.response?.status;
		const message = e?.response?.data?.message || e?.message || 'Update failed';
		throw { status, message };
	}
};
const assertQuotationID = (quotationID: unknown): void => {
	if (!quotationID || quotationID === 'undefined' || quotationID === 'null') {
		throw new Error('A quotation must be created before its areas can be loaded.');
	}
};
export const addAreaToQuotation = async (quotationID, data): Promise<AxiosResponse> => {
	assertQuotationID(quotationID);
	return await put(`quotation/${quotationID}/area`, data);
};
export const changeAreaNickname = async (quotationID, data): Promise<AxiosResponse> =>
	await put(`quotation/${quotationID}/nickname`, data);
export const uploadDocument = async (data): Promise<AxiosResponse> => await put(`document`, data);
export const changeQuotationImage = async (data): Promise<AxiosResponse> => await put(`quotation/product`, data);
export const copyQuotationItem = async (quotationID, data): Promise<AxiosResponse> =>
	await put(`quotation/${quotationID}/copy`, data);
export const updateFCBuilder = async (data): Promise<AxiosResponse> => await post(`builder/fc/calculate`, data);
export const fetchFCBuilderData = async (productId): Promise<AxiosResponse> => await get(`builder/fc/${productId}`);

export const importQuotation = async (
	quotationId: number | string,
	data: { target_project_id: number; version: number; target_areas?: Array<{ area_name: string; floor: number }> }
): Promise<AxiosResponse> => await put(`quotation/${quotationId}/import`, data);

export const fetchLeadCredits = async (userId: number): Promise<AxiosResponse> => await get(`credits/${userId}`);
export const addLeadCredits = async (userId: number, credits: number): Promise<AxiosResponse> =>
	await put(`credits/${userId}/limit?credits=${credits}`);
export const getLeadSourceHistory = async (leadID: any): Promise<AxiosResponse> => await get(`lead/${leadID}/source`);
export const getLeadRemarks = async (leadId: string | number): Promise<AxiosResponse> =>
	await get(`lead/${leadId}/remark`);
export const putLeadRemark = async (leadId: string | number, body: { remark: string }): Promise<AxiosResponse> =>
	await put(`lead/${leadId}/remark`, body);

export const getQuotationExport = async (quotationId: string | number): Promise<AxiosResponse<Blob>> =>
	await getBlob(`quotation/${quotationId}/export`);

export const fetchAddendumQuotation = async (
	quotationId: string,
	addendumId: string
): Promise<AxiosResponse> => await get(`quotation/${quotationId}/addendum/${addendumId}`);

export const sendAddendumToCustomer = async (
	quotationId: string,
	addendumId: string
): Promise<AxiosResponse> => await post(`quotation/${quotationId}/addendum/${addendumId}/send`);

export const exportAddendumQuotation = async (
	quotationId: string,
	addendumId: string
): Promise<AxiosResponse<Blob>> => await getBlob(`quotation/${quotationId}/addendum/${addendumId}/export`);

/** Initiate Exotel call to lead. Requires LEAD_EDIT. */
export const initiateLeadCall = async (
	leadId: string | number
): Promise<AxiosResponse<InitiateCallResponse>> => await post(`lead/${leadId}/call`);

/** Fetch Exotel call history for a lead. Requires LEAD_READ. */
export const getLeadCallHistory = async (
	leadId: string | number
): Promise<AxiosResponse<CallHistoryResponse>> => await get(`exotel/calls/history/lead/${leadId}`);

export const regenerateCutlist = async (quotationId: string | number): Promise<AxiosResponse> =>
	await get(`quotation/${quotationId}/cutlist/regenerate`);

export const startRefreshQuotationCostingAsync = async (quotationId: string | number): Promise<AxiosResponse> =>
	await post(`quotation/${quotationId}/refresh-costing/async`);

export const getRefreshQuotationCostingJob = async (jobId: string): Promise<AxiosResponse> =>
	await get(`quotation/refresh-costing/jobs/${jobId}`);

export const getQuotationCostingSyncStatus = async (quotationId: string | number): Promise<AxiosResponse> =>
	await get(`quotation/${quotationId}/costing-sync-status`);
